import { parseZonedDateTime } from '@internationalized/date';

import { ExecutionRunDetailRecord, NodeExecutionRecord } from '../types';

import { automationTimestampsFragment } from './fragments';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const executionRunDetailQueryAtom = createQueryAtomFactory(
  'automations.executionRun.detail',
  (params: { id: string }) => ({
    executionRun: {
      __args: {
        id: params.id,
      },
      id: true,
      automationId: true,
      status: true,
      triggeredAt: true,
      triggerPayload: true,
      startedAt: true,
      completedAt: true,
      durationMs: true,
      errorMessage: true,
      workflowId: true,
      workflowRunId: true,
      isTest: true,
      nodesExecuted: true,
      actionsExecuted: true,
      executionDurationMs: true,
      nodeExecutions: {
        nodeId: true,
        nodeType: true,
        status: true,
        startedAt: true,
        completedAt: true,
        durationMs: true,
        input: true,
        output: true,
        errorMessage: true,
      },
      organizationId: true,
      timestamps: automationTimestampsFragment,
    },
  }),
  {
    refetchInterval: 30_000,
    select: ({ executionRun }): ExecutionRunDetailRecord | null => {
      if (!executionRun) return null;

      type NodeExecutionGraphQL = {
        nodeId: string;
        nodeType: string;
        status: string;
        startedAt: string;
        completedAt: string;
        durationMs: number;
        input?: Record<string, unknown> | null;
        output?: Record<string, unknown> | null;
        errorMessage?: string | null;
      };

      return {
        id: executionRun.id,
        automationId: executionRun.automationId,
        status: executionRun.status,
        triggeredAt: parseZonedDateTime(executionRun.triggeredAt),
        triggerPayload: executionRun.triggerPayload ?? null,
        startedAt: executionRun.startedAt
          ? parseZonedDateTime(executionRun.startedAt)
          : null,
        completedAt: executionRun.completedAt
          ? parseZonedDateTime(executionRun.completedAt)
          : null,
        durationMs: executionRun.durationMs ?? null,
        errorMessage: executionRun.errorMessage ?? null,
        workflowId: executionRun.workflowId,
        workflowRunId: executionRun.workflowRunId,
        isTest: executionRun.isTest ?? false,
        nodesExecuted: executionRun.nodesExecuted ?? null,
        actionsExecuted: executionRun.actionsExecuted ?? null,
        executionDurationMs: executionRun.executionDurationMs ?? null,
        nodeExecutions: (
          executionRun.nodeExecutions as NodeExecutionGraphQL[]
        ).map(
          (node): NodeExecutionRecord => ({
            nodeId: node.nodeId,
            nodeType: node.nodeType,
            status: node.status as 'Success' | 'Failed' | 'Skipped',
            startedAt: parseZonedDateTime(node.startedAt),
            completedAt: parseZonedDateTime(node.completedAt),
            durationMs: node.durationMs,
            input: node.input ?? null,
            output: node.output ?? null,
            errorMessage: node.errorMessage ?? null,
          }),
        ),
        organizationId: executionRun.organizationId ?? '',
        timestamps: {
          createdAt: parseZonedDateTime(executionRun.timestamps.createdAt),
          updatedAt: parseZonedDateTime(executionRun.timestamps.updatedAt),
        },
      };
    },
  },
);
